import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import Chart from "chart.js/auto";

// Time columns
const timeLabels = [
  "07:00", "08:00", "09:00", "10:00",
  "11:00", "12:00", "13:00", "14:00"
];

// Parameters to chart
const chartParams = [
  "N2O/O2/Air (%)", "Oxygen FiO2 (%)", "Vapour Conc. (%)",
  "End Tidal CO2 (%)", "O2 Saturation (%)", "Core Temperature",
  "IV Fluids (ml/hr)", "Blood Loss (ml)", "Urine Output (ml)"
];

// All parameters including charted and non-charted ones
const allParams = [
  ...chartParams,
  "ECG Rhythm",
  "Tourniquet Time"
];

// Suggestion values for charted parameters
const parameterSuggestions = {
  "N2O/O2/Air (%)": ["50", "60", "70"],
  "Oxygen FiO2 (%)": ["21", "40", "60", "100"],
  "Vapour Conc. (%)": ["0.5", "1.0", "2.0"],
  "End Tidal CO2 (%)": ["35", "40", "45"],
  "O2 Saturation (%)": ["95", "98", "100"],
  "Core Temperature": ["36.5", "37.0", "38.0"],
  "IV Fluids (ml/hr)": ["100", "200", "500"],
  "Blood Loss (ml)": ["100", "250", "500", "1000"],
  "Urine Output (ml)": ["50", "100", "200"]
};

// Create empty grid
const emptyGrid = () => allParams.map(() => Array(timeLabels.length).fill(""));

const AdvancedMonitoringGrid = forwardRef((props, ref) => {
  const [gridData, setGridData] = useState(emptyGrid());
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const isTourniquetRow = (param) => param === "Tourniquet Time";
  const hasSuggestions = (param) => parameterSuggestions.hasOwnProperty(param);

  // Expose functions via ref
  useImperativeHandle(ref, () => ({
    getGridData: () => gridData,
    setGridData: (data) => {
      if (Array.isArray(data) && data.length === allParams.length) {
        const newData = data.map(row =>
          Array.isArray(row) ? row.slice() : Array(timeLabels.length).fill("")
        );
        setGridData(newData);
        setTimeout(updateChart, 100);
        props.onChange?.(newData);
      }
    }
  }));

  // Restrict number input for charted fields
  const restrictToNumbers = (e) => {
    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", ".", "-"];
    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  // Update chart
  const updateChart = () => {
    if (!chartInstance.current) return;
    chartInstance.current.data.datasets.forEach((dataset, i) => {
      if (!gridData[i]) return;
      dataset.data = gridData[i].map(val => {
        const num = parseFloat(val);
        return isNaN(num) ? null : num;
      });
    });
    chartInstance.current.update();
  };

  // Initialize chart
  useEffect(() => {
    if (chartRef.current && !chartInstance.current) {
      chartInstance.current = new Chart(chartRef.current, {
        type: "line",
        data: {
          labels: timeLabels,
          datasets: chartParams.map((param, i) => ({
            label: param,
            data: [],
            borderColor: `hsl(${i * 40}, 70%, 50%)`,
            fill: false,
            tension: 0.3
          }))
        },
        options: {
          responsive: true,
          interaction: { mode: "index", intersect: false },
          scales: { y: { beginAtZero: true } },
          plugins: { legend: { position: "bottom" } }
        }
      });
    }
    updateChart();
    // eslint-disable-next-line
  }, [gridData]);

  return (
    <div className="bg-white p-4 rounded shadow-md">
      <div className="overflow-x-auto">
        <table
          id="advancedMonitoringTable"
          className="min-w-full w-full text-sm text-center border border-gray-300"
        >
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1">Parameter/Time</th>
              {timeLabels.map(time => (
                <th key={time} className="border px-2 py-1">{time}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allParams.map((param, rowIdx) => (
              <tr key={param} data-parameter={param}>
                <td className="border font-medium bg-gray-50 px-2 py-1">{param}</td>
                {timeLabels.map((_, colIdx) => (
                  <td
                    key={colIdx}
                    className="border bg-white px-2 py-1 hover:bg-yellow-50 focus-within:bg-yellow-100 relative"
                  >
                    <input
                      className="w-full px-1 py-1 bg-transparent focus:outline-none text-sm"
                      type={isTourniquetRow(param) ? "time" : "text"}
                      value={gridData[rowIdx][colIdx]}
                      list={hasSuggestions(param) ? `${param.replace(/\s+/g, '')}-list` : undefined}
                      onChange={e => {
                        const newGrid = gridData.map(row => row.slice());
                        newGrid[rowIdx][colIdx] = e.target.value;
                        setGridData(newGrid);
                        props.onChange?.(newGrid);
                      }}
                      onKeyPress={
                        rowIdx < chartParams.length && !isTourniquetRow(param)
                          ? restrictToNumbers
                          : undefined
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Auto-fill suggestion lists */}
      {Object.entries(parameterSuggestions).map(([param, values]) => (
        <datalist id={`${param.replace(/\s+/g, '')}-list`} key={param}>
          {values.map(val => (
            <option key={val} value={val} />
          ))}
        </datalist>
      ))}

      <div className="mt-6 text-sm text-gray-600 font-medium">
        Live Chart (Only Numeric Parameters)
      </div>
      <canvas ref={chartRef} height="140" className="mt-2" />
    </div>
  );
});

export default AdvancedMonitoringGrid;
